// SPDX-License-Identifier: Apache-2.0
// Copyright 2026 The Zyra Project

/**
 * Docent Context — builds LLM system prompts and manages conversation history.
 *
 * Provides the LLM with enough context about available datasets
 * and the current view to give informed, conversational responses.
 */

import type { Dataset, ChatMessage, MapViewContext, ReadingLevel } from '../types'
import type { LLMMessage, LLMTool } from './llmProvider'
import { getLocale, SOURCE_LOCALE, t } from '../i18n'

// --- Constants ---
const MAX_HISTORY_MESSAGES = 50
const RECENT_EXCHANGE_COUNT = 3 // keep last N exchanges (user+docent pairs) verbatim

/**
 * Build a compact summary of available dataset categories with counts.
 */
export function buildCategorySummary(datasets: Dataset[]): string {
  const catCounts = new Map<string, number>()
  for (const d of datasets) {
    if (d.enriched?.categories) {
      for (const cat of Object.keys(d.enriched.categories)) {
        catCounts.set(cat, (catCounts.get(cat) ?? 0) + 1)
      }
    }
  }

  const sorted = Array.from(catCounts.entries())
    .sort((a, b) => b[1] - a[1])

  return sorted.map(([cat, count]) => `- ${cat} (${count})`).join('\n')
}

/**
 * Build a compact description of the currently loaded dataset.
 */
export function buildCurrentDatasetContext(
  dataset: Dataset | null,
  legendDescription?: string | null,
  currentTime?: string | null,
): string {
  if (!dataset) {
    return 'The user is currently viewing the default Earth globe with real-time cloud cover. No specific dataset is loaded. IMPORTANT: Even if you previously suggested a dataset, the user has NOT loaded it unless it appears here as "Currently loaded".'
  }

  const parts: string[] = [
    `Currently loaded: "${dataset.title}"`,
  ]

  const desc = dataset.enriched?.description ?? dataset.abstractTxt
  if (desc) {
    const short = desc.length > 400 ? desc.substring(0, 400) + '…' : desc
    parts.push(`Description: ${short}`)
  }

  const cats = Object.keys(dataset.enriched?.categories ?? {})
  if (cats.length > 0) {
    parts.push(`Categories: ${cats.join(', ')}`)
  }

  const keywords = dataset.enriched?.keywords
  if (keywords && keywords.length > 0) {
    parts.push(`Keywords: ${keywords.slice(0, 10).join(', ')}`)
  }

  if (dataset.organization) {
    parts.push(`Source: ${dataset.organization}`)
  }

  if (dataset.startTime && dataset.endTime) {
    parts.push(`Time range: ${dataset.startTime} to ${dataset.endTime}`)
  }

  const related = dataset.enriched?.relatedDatasets
  if (related && related.length > 0) {
    parts.push(`Related datasets: ${related.map(r => r.title).join(', ')}`)
  }

  if (currentTime) {
    parts.push(`Current time shown: ${currentTime}`)
  }

  if (legendDescription) {
    parts.push(`Legend: ${legendDescription}`)
  }

  // §A6. The exact scale, where the row carries one.
  //
  // `Legend:` above it is a best-effort transcription of the legend
  // *image* by a background vision call — useful prose, and not
  // something to state a range from. This is the publisher's own
  // numbers, so a question like "what does the scale go up to?" has an
  // answer that does not depend on a model having read a picture
  // correctly. Emitted for any data-encoded row, independent of whether
  // the analysis tools resolved: the range is a property of the
  // dataset, where the tools need a decoded frame.
  const scale = dataset.colorScale
  if (scale && Number.isFinite(scale.vmin) && Number.isFinite(scale.vmax)) {
    const units = scale.units ? ` ${scale.units}` : ''
    parts.push(
      `Value scale: ${scale.vmin} to ${scale.vmax}${units}. ` +
      'These are the dataset\'s own published limits, not an estimate. ' +
      'You may state this range. Any *specific* value still has to come from a tool result.',
    )
  }

  return parts.join('\n')
}

/** Maps each reading level to system prompt instructions for tone and vocabulary. */
const READING_LEVEL_INSTRUCTIONS: Record<ReadingLevel, string> = {
  'young-learner': `## Reading Level: Young Learner
Explain everything as if talking to a curious 10-year-old. Use simple, everyday words and short sentences. Compare scientific concepts to things kids experience — weather, animals, food, sports, playground activities. Show excitement and wonder ("Wow!", "Cool, right?"). Avoid jargon entirely; if a science term is necessary, immediately explain it in plain language. Keep responses under 100 words.`,

  'general': '', // default — no extra instructions needed

  'in-depth': `## Reading Level: In-Depth
Provide thorough, informative responses suitable for a scientifically curious adult. Use proper scientific terminology but define technical terms on first use. You may exceed the 150-word limit up to 250 words when the topic warrants it. Explain the "how" and "why" behind phenomena, mention relevant measurement methods or data sources when appropriate, and connect topics to broader Earth system processes.`,

  'expert': `## Reading Level: Expert
Respond at a professional/graduate science level. Use precise scientific terminology freely without defining common domain terms. Focus on data specifics — resolution, temporal coverage, instrumentation, and methodology. Discuss limitations, uncertainties, and how datasets relate to current research. You may ignore the default 150-word limit and use up to 300 words when helpful. Assume the reader has strong background knowledge in Earth sciences.`,
}

/**
 * Build the system prompt for the LLM.
 *
 * The prompt is static with respect to the catalog — it does NOT enumerate
 * datasets, count them, or summarise their categories from the in-memory
 * `datasets` array. Discovery happens through three tools the LLM picks
 * from at runtime, in this priority order (post-1d cutover):
 *
 *   - `search_datasets` (primary) — semantic vector search over the node
 *     catalog backend (Cloudflare Vectorize + Workers AI). Default
 *     discovery tool whenever the user has expressed a topic.
 *   - `list_featured_datasets` — operator-curated list for cold-start
 *     "what should I look at?" conversations (no query).
 *   - `search_catalog` (legacy fallback) — keyword scan over the in-memory
 *     dataset list. Used when `search_datasets` returns empty (typically
 *     a self-hosting deploy that hasn't provisioned Vectorize yet) so
 *     the LLM never has to invent dataset titles from training-time
 *     knowledge.
 *
 * The pre-search `[RELEVANT DATASETS]` injection (Phase 1c) was
 * removed in 1d/F when the cutover relied on the LLM to call
 * `search_datasets` itself, then re-introduced in 1d/AC after live
 * testing showed mid-tier models (llama-4-scout, sometimes 3.1-70b)
 * confabulate id-shaped strings rather than copying tool-result
 * IDs verbatim. The injection's source is now the Vectorize-backed
 * `search_datasets` rather than the legacy in-memory keyword scan,
 * so the architectural goals of the cutover (single search source
 * of truth, federation-ready) are preserved while the docent's
 * chip-render reliability matches the pre-cutover regime.
 *
 * `datasets` is still passed in for the (currently unused) categorySummary
 * helper that other surfaces consume; the prompt itself ignores it.
 */
export function buildSystemPrompt(
  _datasets: Dataset[],
  currentDataset: Dataset | null,
  readingLevel: ReadingLevel = 'general',
  visionActive: boolean = false,
  legendDescription?: string | null,
  currentTime?: string | null,
  qaContext?: string | null,
  mapViewContext?: Parameters<typeof buildViewContextSection>[0],
  /** §A6 — are the value-reading tools being offered this turn?
   *  Computed once by the caller and used for both the prompt and the
   *  tool array, so the permission and the tools can never disagree.
   *  Offering one without the other is a bug in either direction: the
   *  permission alone invites the model to answer from memory, and the
   *  tools alone leave it forbidden from saying what came back. */
  analysisToolsActive: boolean = false,
): string {
  const currentContext = buildCurrentDatasetContext(currentDataset, legendDescription, currentTime)
  const languagePreface = buildLanguagePreface()
  const languageDirective = buildLanguageDirective()

  return `${languagePreface}You are Orbit, a Digital Docent for Science on a Sphere — an interactive 3D globe that visualizes Earth science datasets from NOAA.

Your role is to be a warm, knowledgeable guide. You help visitors explore and understand environmental data by explaining what they're seeing and recommending relevant datasets to load onto the globe.

IMPORTANT: All datasets are GLOBAL — they cover the entire Earth, rendered on a 3D sphere. The user's current view only shows one side of the globe, but the data extends everywhere. Never say a dataset "only shows" one region or "doesn't cover" a location. The user can rotate the globe or use <<FLY:...>> to view any part of the world.

## STRICT RULES — FOLLOW EXACTLY
1. NEVER mention a dataset by name or ID unless it appears in one of these sources: the [RELEVANT DATASETS] block in the user's message, a \`search_datasets\` / \`list_featured_datasets\` / \`search_catalog\` tool result, or the Current View section (for the currently loaded dataset). Do not invent, guess, or paraphrase dataset titles. **NO EXCEPTIONS for "related" datasets, "similar" datasets, "another option", or anything else** — if you want to suggest a related dataset, you MUST first call a discovery tool with a relevant query and use what comes back. Mentioning a dataset name without a corresponding tool-result entry produces a broken Load chip and a dangling sentence in the user's chat — a worse UX than not suggesting the dataset at all.
2. NEVER describe what a dataset contains beyond what the tool result and the Reference Knowledge section say. Do not invent data values, date ranges, or trends.
3. If a discovery tool returns one or more results, treat them as legitimate recommendations — present them by title with \`<<LOAD:...>>\` markers immediately. Do NOT preface them with "I don't have a dataset for that specific topic" or any similar apology — that phrase is ONLY for the case where the tool returns a truly empty array with zero entries. If the results are semantically adjacent rather than an exact keyword match, you may say "Here are some related datasets:" or "The closest matches I found:" — but still present them confidently with markers, not as non-matches.
4. ONLY discuss Earth science, environmental data, weather, climate, oceans, geology, space science, ecology, and the datasets in this collection.
5. DECLINE off-topic requests politely: "That's outside my area! I'm here to help you explore Earth science data. Try asking about weather, oceans, climate, volcanoes, or space — or say 'show me something interesting'!"${analysisToolsActive ? `
6. The dataset on screen carries REAL VALUES and you have tools that read them. A question about **what the data on screen says** — how much, how bad, where is it worst, what is it at this place — is answered by CALLING A TOOL (\`find_extremum\`, \`summarize_region\`, \`probe_value\`), never by estimating from the colours and never by recommending a different dataset. Recommending another dataset in place of measuring this one is the single most common way to get this wrong. See "Answering about values" below.` : ''}

## Current View (SOURCE OF TRUTH — always check this before assuming what the user sees)
${currentContext}${buildViewContextSection(mapViewContext ?? null)}${analysisToolsActive ? `\n${ANALYSIS_PROMPT_CARVE_OUT}` : ''}
${qaContext ? `\n## Reference Knowledge\nUse the following Q&A excerpts to inform your answer. Paraphrase — do not quote verbatim.\n${qaContext}\n` : ''}
## Available Categories
The collection covers global Earth science datasets across categories such as Atmosphere, Oceans, Climate, Geology, Land Surface, Hydrosphere, Cryosphere, Biosphere, Ecology, and Space Science.

## Finding and Recommending Datasets
You do NOT have the full dataset catalog in your context window. Datasets to recommend come from these sources, in priority order:

1. **[RELEVANT DATASETS] in the user message** — when present, this block contains pre-searched results with \`id\`, \`title\`, \`categories\`, and a description snippet, sourced from the same Vectorize semantic search the \`search_datasets\` tool would call. **Use these FIRST.** They are the highest-confidence source for the current query and you do not need to call any discovery tool when they are present — every recommendation should pull its \`id\` from this block.
2. **\`search_datasets\` tool** — semantic vector search over the node catalog. Use this when the [RELEVANT DATASETS] block is missing or doesn't have a suitable match for a follow-up query. Result shape: \`{ datasets: [{ id, title, abstract_snippet, categories, peer_id, score }] }\`.
3. **\`list_featured_datasets\` tool** — small operator-curated list (no query). Use it for cold-start prompts ("show me something interesting", "what should I look at?", "I don't know where to start") when the user has not expressed a topic. Returns \`{ datasets: [{ id, title, abstract_snippet, thumbnail_url, categories, position }] }\`.
4. **\`search_catalog\` tool** — legacy in-memory keyword scan over the catalog. **Use this ONLY as a last-ditch fallback** when both [RELEVANT DATASETS] and \`search_datasets\` come up empty (typically a self-hosting deploy without Vectorize provisioned). Returns up to 10 results with \`id\`, \`title\`, \`categories\`, \`description\`. **Never invent dataset titles or IDs from training-time knowledge.**

**ID INTEGRITY** — IDs returned by these tools are the ONLY valid \`<<LOAD:...>>\` payloads. If you cannot find an exact ID for a title in any of the sources above, do not include the title in your reply at all. The frontend strips markers whose ID isn't recognised; a stripped marker yields a title with no Load button — a worse UX than not mentioning the dataset. **Never invent or guess at any ID format. Do not generate a string that looks like an ID. The only valid IDs are the literal characters you receive in a tool result.**

**CALL TOOLS SILENTLY.** If you call a discovery tool, do NOT narrate it in text. Never write "Here's a search for...", "Let me check the catalog", "I'll search for...", "Searching...", or similar. The user never sees your internal reasoning — only your final prose.

WORKFLOW:
1. Check whether the user message contains a [RELEVANT DATASETS] block. If so, pick the best 1–3 matches from it; you do not need to call any discovery tool. Copy each row's \`id\` field verbatim into the marker.
2. Otherwise — if [RELEVANT DATASETS] is absent OR the user is asking a follow-up about a different topic than the block covers — call \`search_datasets\` silently. If \`search_datasets\` returns an empty \`datasets\` array, fall back to \`search_catalog\` (or \`list_featured_datasets\` for cold-start prompts) before giving up — never invent dataset titles or IDs.
3. Recommend the chosen 1–3 matches in prose, referring to each by its exact \`title\`.
4. **MANDATORY**: Every dataset title you mention MUST be immediately followed by a \`<<LOAD:FULL_DATASET_ID>>\` marker on its own line, using the exact \`id\` field from whichever source provided it ([RELEVANT DATASETS] or a tool result). This is non-negotiable — without the marker the user cannot click to load the dataset and your recommendation is useless. Mentioning a title in prose without the marker is a bug, not an option.

WORKFLOW EXAMPLE — user asks about hurricanes.

What you DO (internal — never visible to the user):
  1. Call the \`search_datasets\` tool with a query derived from the user's question.
  2. The tool returns a JSON object with a \`datasets\` array. Each entry has an \`id\` string field.
  3. Copy each \`id\` string literally into the marker payloads of the reply below — character for character, no edits, no shortening, no synthesis.

What you SAY (this is the ENTIRE reply visible to the user — no other text, no prefaces, no annotations):
  Here's a dataset showing hurricane tracks over 70 years.
  <<LOAD:COPY_THE_FIRST_ID_FIELD_HERE>>
  Another option focuses on wind patterns.
  <<LOAD:COPY_THE_SECOND_ID_FIELD_HERE>>

Two non-negotiable rules:

  - The marker payloads in the example above (\`COPY_THE_FIRST_ID_FIELD_HERE\`, \`COPY_THE_SECOND_ID_FIELD_HERE\`) are illustrative names. They are NOT real IDs. Your actual reply MUST replace them with the exact \`id\` strings from the tool result. Never write any placeholder name in a real reply. Never invent an ID by extending a pattern you've seen — IDs come from the tool, period.

  - Never write any of these in a real reply: "(Silently)", "Internal:", "Step 1", "Step 2", "I'll search for", "Let me check", "Searching the catalog", "What you DO", "What you SAY", or any other narration of your reasoning or tool use. The user does NOT see your tool calls or your thought process — they only see the final prose. Annotations like the ones in this example are FOR YOU; they must never appear in your output.

You may call discovery tools multiple times in the same turn with different queries if the first call isn't useful, or to cross-reference related topics. But be efficient — don't search for things you've already searched for in this conversation, and don't narrate the additional searches either.

**Following up with "related" suggestions** — if you want to extend your reply with "Here are some related datasets…" or "Another option is…", that follow-on MUST come from a tool call you actually make in this turn. Do not list "related" datasets you remember from elsewhere — the user has no chip to click and your sentence reads as half-formed prose.

CRITICAL RULES — violations break the UI:
- NEVER write a dataset ID anywhere in your prose text. IDs must ONLY appear inside <<LOAD:...>> markers.
- Refer to datasets by their TITLE in prose, never by ID. The marker carries the ID silently.
- EVERY dataset you mention must have a <<LOAD:...>> marker. No exceptions.
- NEVER say "I'll load", "let me load", "I've loaded", "loading this would", or similar. Just place the <<LOAD:...>> marker and move on. The marker automatically creates a Load button for the user.
- Do NOT ask the user if they want to load, and do NOT describe what loading would do — just include the marker.
- NEVER assume a dataset is loaded just because you suggested it in a previous message. ALWAYS check the "Current View" section above to see what is actually on the globe right now. If it's not loaded, include the <<LOAD:...>> marker again.
- Use the FULL ID exactly as returned by the discovery tool — copy the entire id string verbatim, character for character. Do not abbreviate, truncate, or generate ID-shaped strings.

## Globe Control Markers
You can control the globe view by placing markers in your text, just like <<LOAD:...>> markers:
- <<FLY:lat,lon>> or <<FLY:lat,lon,altitude_km>> — Animate the camera to a location. Altitude in km is optional.
- <<TIME:ISO_DATE>> — Seek a time-enabled dataset to a specific date.
- <<LOAD_FRAME:DATASET_ID:query>> — Load a SINGLE FRAME from an image-sequence dataset. ONLY use this for datasets whose discovery-tool result includes a \`frames\` field with non-null \`count\`. The query is one of: an ISO 8601 timestamp like \`2026-05-16T12:00:00Z\` (the closest frame is chosen), \`index=N\` (zero-based), a bare integer (\`47\`), or \`latest\` / \`first\`. Use this when the user asks for a specific date or moment inside a time series — "show me the SST anomaly for May 16" — instead of <<LOAD:...>> which loads the whole sequence. Skip the marker entirely if the dataset has no \`frames\` envelope.

Place these on their own line. Example — user asks about Hurricane Katrina:
Here's a look at the 2005 hurricane season.
<<LOAD:HURRICANE_DATASET_ID_FROM_TOOL_RESULT>>
<<FLY:29.0,-89.0,3000>>
<<TIME:2005-08-29>>

Additional globe markers:
- <<BOUNDS:west,south,east,north>> or <<BOUNDS:west,south,east,north,label>> — Navigate to a bounding box (e.g. <<BOUNDS:-82,-34,-34,13,Amazon Basin>>)
- <<MARKER:lat,lng,label>> — Place a labeled marker on the globe (e.g. <<MARKER:35.7,139.7,Tokyo>>)
- <<LABELS:on>> or <<LABELS:off>> — Show or hide geographic labels and boundaries
- <<REGION:name>> — Highlight a well-known geographic region and navigate to it. Supported regions include countries (e.g. <<REGION:Brazil>>), continents (<<REGION:Africa>>), oceans (<<REGION:Pacific Ocean>>), and geographic features (<<REGION:Amazon Basin>>, <<REGION:Ring of Fire>>, <<REGION:Sahara Desert>>).

## Current Events
When the user's message includes a [CURRENT EVENTS] block, it lists reputable, curator-approved current events relevant to this node's data — each with an \`id\`, a headline, its source, and a date. It is INTERNAL context. Surface one by placing a marker on its own line:
- <<EVENT:ID>> — Show a cited card for that event AND load the dataset that explains it, flying the globe to where and when it happened. The client fills in the location, time, source, and dataset from the approved event — you supply ONLY the ID.

Rules for events:
- NEVER mention the "[CURRENT EVENTS]" block (or any bracketed [...] block) in your reply, and NEVER write an event id or a dataset id in your prose. Those are internal — the user must never see a block name or a raw id. Talk about the event by its **headline** and the dataset by its **title**; the <<EVENT:ID>> marker silently carries the id.
- ONLY use an ID that appears verbatim in a [CURRENT EVENTS] block or a \`search_events\` tool result. NEVER invent an event, headline, source, date, or ID — the only current events that exist are the curator-approved ones in those sources. If none fits the user's question, don't mention one.
- When a headline directly answers the user (e.g. "what's happening with wildfires right now?"), lead with a one-sentence plain-language summary then the <<EVENT:ID>> marker. Do NOT also emit a <<LOAD:...>>, <<FLY:...>>, or <<TIME:...>> marker (and do NOT call the load_dataset / fly_to / set_time tools) for that event — the event marker loads its dataset and moves the globe to the right place and time for you. Adding your own time action risks a spurious "no video dataset loaded" message.
- Refer to the event naturally in prose (e.g. "there's an active wildfire outbreak") but let the marker carry the citation; do not paste the source URL into your text.

IMPORTANT rules for globe markers:
- <<FLY:...>> can be used ANY time the user asks to see a location — it works whether or not a dataset is loaded. If the user asks to fly somewhere, just do it.
- <<TIME:...>> requires a time-enabled dataset to be loaded. Dates MUST fall within the dataset's time range shown in the "Current View" section. Never suggest a date outside the range.
- <<BOUNDS:...>> is great for showing regions, continents, or ocean basins — prefer it over <<FLY:...>> when the user asks about an area rather than a point.
- <<MARKER:...>> is useful to highlight specific locations being discussed. Markers persist on the globe and include a popup that shows the label when clicked.
- <<LABELS:on>> is helpful when discussing geographic context — show labels to orient the user, hide them when they clutter the data view.
- <<REGION:...>> is the best way to highlight a region by name. It draws a highlighted box on the globe and navigates to it. Prefer this over <<BOUNDS:...>> when a well-known region name is available.
- Never write fly_to(...), set_time(...), fit_bounds(...), highlight_region(...), or similar function-call syntax in your text. Use ONLY the marker format above.

## Guidelines
- Be conversational and enthusiastic about science, but concise
- Refer to datasets by their human-readable title, never by ID
- When explaining a dataset, focus on what it reveals about our planet and why it matters
- If the user asks about a topic, find relevant datasets and explain what they show
- If asked "what is this" or "explain", describe the currently loaded dataset
- Suggest related datasets when relevant — help users discover connections between Earth systems. **But: you MUST call a discovery tool first to get IDs for the related datasets.** Do not pull "related" dataset names from memory or training-time knowledge — those produce broken Load chips. If you want to recommend a follow-on dataset on a different topic, run a fresh \`search_datasets\` call (e.g. \`search_datasets({ query: "sea ice extent" })\`) and use what it returns. If it returns nothing useful, end your reply without inventing one.
- Datasets marked [Tour] are guided experiences that walk users through a topic with narration, camera movements, and interactive questions. Recommend tours when the user seems new, asks for an overview, or wants to learn about a broad topic. Load them the same way as other datasets with <<LOAD:...>> markers.
- If you don't know something specific, be honest and don't guess — point toward relevant data if possible
- Keep responses under 150 words unless the user asks for detail
- If asked about the dataset legend or color scale: only describe it if a "Legend:" field appears in the Current View section above. If no Legend field is present, say "I don't have the legend details for this dataset right now" — never invent or estimate color scales or value ranges from general knowledge
- REMINDER: Only mention datasets that appear in the [RELEVANT DATASETS] block, a discovery-tool result (\`search_datasets\`, \`list_featured_datasets\`, or \`search_catalog\`), or the Current View section for the currently loaded dataset. Every dataset title you mention must be copied exactly from the source where it appears.${READING_LEVEL_INSTRUCTIONS[readingLevel] ? '\n\n' + READING_LEVEL_INSTRUCTIONS[readingLevel] : ''}${visionActive ? `

## Vision Analysis Mode
CRITICAL: The attached image is a SCIENTIFIC DATA VISUALIZATION rendered on a 3D globe — it is NOT a real photograph of Earth. Every color, pattern, bright spot, and visual feature you see represents DATA VALUES from the currently loaded dataset. Do NOT interpret any feature as a real-world object (not the Moon, not a satellite, not city lights, etc.).
- The user's message starts with metadata in brackets: dataset name, description, coordinates, and time. READ this carefully before answering.
- Describe visual patterns (colors, gradients, vortices, bright/dark areas) and explain them in terms of what the dataset measures.
- Use the coordinates and time to identify the geographic region and temporal context.
- If no dataset is loaded, describe the default Earth view.` : ''}${languageDirective}`
}

/**
 * Resolve the active locale's display name in its own language
 * ("español" for es, "Français" for fr, …). Used by both the
 * preface and the tail directive. Falls back to the BCP-47 tag if
 * `Intl.DisplayNames` fails (no ICU data on a stripped runtime).
 */
function activeLanguageName(): string | null {
  const active = getLocale()
  if (active === SOURCE_LOCALE) return null
  try {
    return new Intl.DisplayNames(active, { type: 'language' }).of(active) ?? active
  } catch {
    return active
  }
}

/**
 * Top-of-prompt language directive. Smaller models weight the first
 * lines of a system prompt more heavily than the last, so the
 * "respond in <language>" rule lives both *before* the role
 * description (here) and as a tail reminder ({@link buildLanguageDirective}).
 * Wave 5/6 testing showed Llama-3.1-70B and similar mid-tier models
 * frequently slipped back into English when the directive was only
 * appended at the end.
 *
 * The directive is rendered in the target language so the LLM sees
 * the instruction in the same language it should reply in — better
 * adherence than instructing in English. <<LOAD:...>> markers and
 * tool-call syntax stay intact per the directive's wording.
 *
 * No-op for English users (returns `''`, which template-interpolates
 * cleanly at the top of the prompt).
 */
function buildLanguagePreface(): string {
  const languageName = activeLanguageName()
  if (!languageName) return ''
  return t('docent.system.respondInLanguage', { language: languageName }) + '\n\n'
}

/**
 * Tail "respond in {language}" reminder, repeated at the end of the
 * prompt for belt-and-suspenders adherence. See
 * {@link buildLanguagePreface} for the higher-weight placement at
 * the top of the prompt. No-op for English users.
 */
function buildLanguageDirective(): string {
  const languageName = activeLanguageName()
  if (!languageName) return ''
  return '\n\n' + t('docent.system.respondInLanguage', { language: languageName })
}

/**
 * Build a freshness-anchored reminder LLM message that goes right
 * before the user's current turn. Mid-tier models tend to mirror
 * the user's input language ("user wrote English → reply English")
 * even with a respond-in-{language} directive at the top of the
 * system prompt — by the time the model generates its final
 * response (post-tool-call), the system prompt is many messages
 * back and gets crowded out by the model's own English tool-call
 * thinking + the tool results.
 *
 * Placing a terse system-role reminder immediately before the
 * user's message keeps the directive in the model's most-recent
 * attention window. Empirically this is the single most effective
 * lever for language adherence on smaller models. Returns `null`
 * for English users (no-op) so callers can safely splat the
 * result with `?? []`.
 */
export function buildLanguageReminderMessage(): LLMMessage | null {
  const languageName = activeLanguageName()
  if (!languageName) return null
  return {
    role: 'system' as const,
    content: t('docent.system.respondInLanguageReminder', { language: languageName }),
  }
}

/**
 * Restore [[LOAD:ID]] placeholders (set by the chat UI for inline buttons)
 * back to <<LOAD:ID>> so the LLM sees a consistent marker format in history.
 */
function restoreLoadMarkers(text: string): string {
  return text.replace(/\[\[LOAD:([^\]]+)\]\]/g, '<<LOAD:$1>>')
}

/**
 * Convert ChatMessage history to LLM message format, trimmed to max length.
 */
export function buildMessageHistory(messages: ChatMessage[]): LLMMessage[] {
  const trimmed = messages.slice(-MAX_HISTORY_MESSAGES)

  return trimmed.map(msg => ({
    role: msg.role === 'user' ? 'user' as const : 'assistant' as const,
    content: msg.role === 'user' ? msg.text : restoreLoadMarkers(msg.text),
  }))
}

/**
 * Summarize older messages into a compact string.
 * Extracts topics from user messages and loaded datasets from docent actions.
 */
export function summarizeOlderMessages(messages: ChatMessage[]): string {
  const topics: string[] = []
  const loadedDatasets: string[] = []

  for (const msg of messages) {
    if (msg.role === 'user') {
      const short = msg.text.length > 60 ? msg.text.substring(0, 60) + '…' : msg.text
      topics.push(short)
    } else if (msg.actions?.length) {
      for (const a of msg.actions) {
        if (a.type === 'load-dataset' && !loadedDatasets.includes(a.datasetTitle)) {
          loadedDatasets.push(a.datasetTitle)
        }
      }
    }
  }

  const parts: string[] = []
  if (topics.length > 0) parts.push(`Topics discussed: ${topics.join('; ')}`)
  if (loadedDatasets.length > 0) parts.push(`Datasets suggested: ${loadedDatasets.join(', ')}`)
  return parts.length > 0
    ? `[Conversation summary: ${parts.join('. ')}.]\n`
    : ''
}

/**
 * Build a compressed message history for the LLM.
 * Last N exchanges are kept verbatim; older messages are summarized.
 */
export function buildCompressedHistory(messages: ChatMessage[]): LLMMessage[] {
  const trimmed = messages.slice(-MAX_HISTORY_MESSAGES)

  // How many recent messages to keep verbatim (N exchanges = 2N messages)
  const recentCount = RECENT_EXCHANGE_COUNT * 2

  if (trimmed.length <= recentCount) {
    return trimmed.map(msg => ({
      role: msg.role === 'user' ? 'user' as const : 'assistant' as const,
      content: msg.role === 'user' ? msg.text : restoreLoadMarkers(msg.text),
    }))
  }

  const older = trimmed.slice(0, -recentCount)
  const recent = trimmed.slice(-recentCount)

  const result: LLMMessage[] = []

  const summary = summarizeOlderMessages(older)
  if (summary) {
    result.push({ role: 'system', content: summary })
  }

  for (const msg of recent) {
    result.push({
      role: msg.role === 'user' ? 'user' as const : 'assistant' as const,
      content: msg.role === 'user' ? msg.text : restoreLoadMarkers(msg.text),
    })
  }

  return result
}

/**
 * Tool definition for the legacy in-memory keyword catalog scan.
 *
 * Post-1d cutover: `search_datasets` (semantic vector search over
 * the node-catalog backend) is the primary discovery tool. This
 * tool stays in the toolset as the empty-result fallback for
 * self-hosting deploys that haven't provisioned Vectorize yet —
 * if `search_datasets` returns an empty array, the prompt instructs
 * the LLM to call `search_catalog` here before giving up so it
 * never has to invent dataset titles from training-time knowledge.
 */
export function getSearchCatalogTool(): LLMTool {
  return {
    type: 'function',
    function: {
      name: 'search_catalog',
      description: 'Search the legacy in-memory dataset catalog by keyword. Use as a fallback when `search_datasets` returns an empty `datasets` array (typically on deploys that have not provisioned Vectorize). Returns up to `limit` matching datasets ranked by keyword overlap, each with id, title, categories, and a short description. Prefer `search_datasets` for first-line discovery — it understands meaning rather than just keywords.',
      parameters: {
        type: 'object',
        properties: {
          query: {
            type: 'string',
            description: 'Keywords or short phrase describing what to search for (e.g. "hurricane tracks", "sea surface temperature", "volcanoes", "ocean acidification"). Broad category names like "weather" or "oceans" also work.',
          },
          limit: {
            type: 'number',
            description: 'Maximum number of results to return. Defaults to 5. Maximum 10.',
            default: 5,
            minimum: 1,
            maximum: 10,
          },
        },
        required: ['query'],
      },
    },
  }
}

/**
 * Tool definition for the node-catalog backend semantic search
 * (`/api/v1/search?q=...`). Post-1d cutover this is the PRIMARY
 * discovery tool — uses Cloudflare Vectorize + Workers AI under the
 * hood, so retrieval is meaning-based rather than keyword-based.
 * Self-hosters who haven't provisioned Vectorize see an empty
 * `datasets` array; the prompt instructs the LLM to fall back to
 * `search_catalog` in that case rather than invent IDs.
 *
 * The tool result shape mirrors `_lib/search-datasets.ts`:
 * `{ datasets: [{ id, title, abstract_snippet, categories, peer_id, score }] }`
 * — small enough that 10 hits fit in any LLM context window.
 */
export function getSearchDatasetsTool(): LLMTool {
  return {
    type: 'function',
    function: {
      name: 'search_datasets',
      description: 'Semantic vector search over the node catalog. Embeds the query via Workers AI, queries Cloudflare Vectorize, returns the top matching datasets ranked by cosine similarity. **PRIMARY discovery tool** — use this whenever the user has expressed a topic. Useful when you have a topical query and want meaning-based matching rather than keyword overlap (e.g. "show me how oceans are warming" matches sea-surface-temperature datasets even without keyword overlap). Returns up to `limit` results, each with `id`, `title`, `abstract_snippet`, `categories`, `peer_id`, and `score`. On deploys without Vectorize provisioned, this returns an empty `datasets` array — fall back to `search_catalog` in that case.',
      parameters: {
        type: 'object',
        properties: {
          query: {
            type: 'string',
            description: 'Natural-language query describing what to search for (e.g. "hurricane tracks", "ocean temperature trends", "wildfire smoke transport across continents"). Full sentences work; the embedder reads them as semantic content, not bag-of-words.',
          },
          limit: {
            type: 'number',
            description: 'Maximum number of results to return. Defaults to 5. Maximum 50 server-side, but 5–10 is the sweet spot for a chat reply.',
            default: 5,
            minimum: 1,
            maximum: 50,
          },
        },
        required: ['query'],
      },
    },
  }
}

/**
 * Tool definition for the operator-curated featured-datasets list
 * (`/api/v1/featured`). Phase 1c — used for cold-start prompts
 * where the user has not yet expressed a topic ("show me something
 * interesting", "I don't know where to start"). Returns a small
 * payload the docent can suggest in chat.
 *
 * The tool result shape mirrors `_lib/featured-datasets.ts`:
 * `{ datasets: [{ id, title, abstract_snippet, thumbnail_url, categories, position }] }`.
 */
export function getListFeaturedDatasetsTool(): LLMTool {
  return {
    type: 'function',
    function: {
      name: 'list_featured_datasets',
      description: 'Return the operator-curated featured datasets in display order. Use this when the user has NOT expressed a topic — cold-start prompts like "show me something interesting", "what should I look at?", "I am new here", "give me an overview". Do NOT use it for topical queries (use `search_datasets` instead). Returns a small list with `id`, `title`, `abstract_snippet`, `thumbnail_url`, `categories`, and `position`.',
      parameters: {
        type: 'object',
        properties: {
          limit: {
            type: 'number',
            description: 'Maximum number of featured datasets to return. Defaults to 6. Maximum 24.',
            default: 6,
            minimum: 1,
            maximum: 24,
          },
        },
      },
    },
  }
}

/**
 * Tool definition for searching approved current events
 * (`docs/CURRENT_EVENTS_PLAN.md` §6.2). The docent surfaces the returned
 * events with `<<EVENT:ID>>` markers. Executed client-side as an in-memory
 * keyword filter over the approved, in-window events already fetched for
 * the turn (mirrors `search_catalog`), so it needs no Vectorize and works
 * on any deploy. The `[CURRENT EVENTS]` injection block covers the
 * cold-start case; this tool is for follow-up queries where the block is
 * absent or a different topic than what's injected.
 *
 * Result shape: `{ events: [{ id, title, source_name, occurred }] }` — no
 * dataset id is exposed to the model (the marker resolves the dataset
 * client-side), so it can't leak one into prose. Only IDs returned here (or
 * present in a [CURRENT EVENTS] block) are valid `<<EVENT:ID>>` payloads —
 * the anti-hallucination rule for events.
 */
export function getSearchEventsTool(): LLMTool {
  return {
    type: 'function',
    function: {
      name: 'search_events',
      description: 'Search reputable, curator-approved current events relevant to this node\'s data (recent news / authoritative-org reporting tied to a dataset). Use this when the user asks what is happening "right now", references a news story, or wants the current real-world context behind the data — and the [CURRENT EVENTS] block is absent or does not cover their topic. Returns matching approved events, each with `id`, `title`, `source_name`, and `occurred` (date). Surface a result with an `<<EVENT:ID>>` marker — the client loads the explaining dataset for you (never write the id or a dataset id in your reply). Returns an empty `events` array when nothing matches or no events are approved — never invent one.',
      parameters: {
        type: 'object',
        properties: {
          query: {
            type: 'string',
            description: 'Natural-language topic to match against approved event headlines and summaries (e.g. "wildfires", "hurricane in the Atlantic", "flooding"). Leave empty to list all approved current events.',
          },
          limit: {
            type: 'number',
            description: 'Maximum number of events to return. Defaults to 5. Maximum 20.',
            default: 5,
            minimum: 1,
            maximum: 20,
          },
        },
      },
    },
  }
}

/**
 * The tool definition for loading a dataset onto the globe.
 */
export function getLoadDatasetTool(): LLMTool {
  return {
    type: 'function',
    function: {
      name: 'load_dataset',
      description: 'Load a specific dataset onto the 3D globe for the user to view. Use this when recommending a dataset to the user. You should call `search_datasets` (or `search_catalog` as a fallback) first to find the correct dataset id before calling this tool.',
      parameters: {
        type: 'object',
        properties: {
          dataset_id: {
            type: 'string',
            description: 'The dataset ID — the literal value of the `id` field from a prior discovery-tool result, copied verbatim. Do not abbreviate, truncate, or invent.',
          },
          dataset_title: {
            type: 'string',
            description: 'The human-readable dataset title, from the matching discovery-tool result.',
          },
        },
        required: ['dataset_id', 'dataset_title'],
      },
    },
  }
}

/**
 * Phase 3pg/C — tool definition for loading a single frame from
 * an image-sequence dataset. Function-calling providers that
 * prefer tools to inline markers emit this; markdown-style
 * providers emit `<<LOAD_FRAME:DATASET_ID:query>>` instead. The
 * SPA pipeline accepts both shapes.
 */
export function getLoadFrameTool(): LLMTool {
  return {
    type: 'function',
    function: {
      name: 'load_frame',
      description:
        'Load a single frame from an image-sequence dataset (one whose discovery-tool result includes a `frames` field with non-null `count`). Use this when the user asks for a specific date or index inside a time-series — e.g. "show me the SST anomaly for May 16" — instead of loading the whole sequence.',
      parameters: {
        type: 'object',
        properties: {
          dataset_id: {
            type: 'string',
            description:
              'The dataset ID — literal `id` value from a prior discovery-tool result that has a `frames` envelope. Do not invent or abbreviate.',
          },
          dataset_title: {
            type: 'string',
            description:
              'The human-readable dataset title from the matching discovery-tool result.',
          },
          query: {
            type: 'string',
            description:
              'Which frame to load. One of: an ISO 8601 timestamp like `2026-05-16T12:00:00Z`, `index=N` (zero-based), a bare integer (`47`), or `latest` / `first`. The client resolves to the closest frame for timestamp queries.',
          },
        },
        required: ['dataset_id', 'dataset_title', 'query'],
      },
    },
  }
}

/**
 * Tool definition for flying the globe camera to a geographic location.
 */
export function getFlyToTool(): LLMTool {
  return {
    type: 'function',
    function: {
      name: 'fly_to',
      description: 'Smoothly animate the globe camera to center on a specific geographic location. You can provide lat/lon coordinates, or a place name (country, state, region, ocean) to fly to its center automatically. If both are provided, lat/lon takes priority.',
      parameters: {
        type: 'object',
        properties: {
          lat: {
            type: 'number',
            description: 'Latitude in degrees (-90 to 90)',
          },
          lon: {
            type: 'number',
            description: 'Longitude in degrees (-180 to 180)',
          },
          place: {
            type: 'string',
            description: 'A well-known place name to fly to (e.g. "Colorado", "Japan", "Amazon Basin"). Resolves to the center of the region. Use this instead of lat/lon when you know the place name but not the exact coordinates.',
          },
          altitude: {
            type: 'number',
            description: 'Viewing altitude in kilometers above the surface. Lower values zoom in closer (min ~950 km), higher values show more of the globe (max ~16,500 km). Default: keep current altitude.',
          },
        },
      },
    },
  }
}

/**
 * Tool definition for seeking a time-enabled dataset to a specific date.
 */
export function getSetTimeTool(): LLMTool {
  return {
    type: 'function',
    function: {
      name: 'set_time',
      description: 'Seek the currently loaded time-enabled dataset to a specific date/time. Only works when a video dataset with temporal data is loaded. Use this to show a particular moment in time, such as a storm event or seasonal change.',
      parameters: {
        type: 'object',
        properties: {
          date: {
            type: 'string',
            description: 'ISO 8601 date or datetime string (e.g. "2005-08-29" or "2005-08-29T12:00:00Z")',
          },
        },
        required: ['date'],
      },
    },
  }
}

/**
 * Tool definition for navigating to a bounding box.
 */
export function getFitBoundsTool(): LLMTool {
  return {
    type: 'function',
    function: {
      name: 'fit_bounds',
      description: 'Animate the globe camera to fit a geographic bounding box. Use this to show a specific region like a country, ocean basin, or continent.',
      parameters: {
        type: 'object',
        properties: {
          west: { type: 'number', description: 'Western longitude (-180 to 180)' },
          south: { type: 'number', description: 'Southern latitude (-90 to 90)' },
          east: { type: 'number', description: 'Eastern longitude (-180 to 180)' },
          north: { type: 'number', description: 'Northern latitude (-90 to 90)' },
          label: { type: 'string', description: 'Optional label for the region (e.g. "Amazon Basin")' },
        },
        required: ['west', 'south', 'east', 'north'],
      },
    },
  }
}

/**
 * Tool definition for placing a labeled marker on the globe.
 */
export function getAddMarkerTool(): LLMTool {
  return {
    type: 'function',
    function: {
      name: 'add_marker',
      description: 'Place a labeled marker on the globe at a specific location. Use this to highlight a point of interest discussed in the conversation.',
      parameters: {
        type: 'object',
        properties: {
          lat: { type: 'number', description: 'Latitude (-90 to 90)' },
          lng: { type: 'number', description: 'Longitude (-180 to 180)' },
          label: { type: 'string', description: 'Label text for the marker popup' },
        },
        required: ['lat', 'lng'],
      },
    },
  }
}

/**
 * Tool definition for toggling geographic labels and boundaries.
 */
export function getToggleLabelsTool(): LLMTool {
  return {
    type: 'function',
    function: {
      name: 'toggle_labels',
      description: 'Show or hide geographic labels (country names, city names, ocean names) and political boundaries/coastlines on the globe. Both labels and boundaries are toggled together.',
      parameters: {
        type: 'object',
        properties: {
          visible: { type: 'boolean', description: 'true to show labels, false to hide them' },
        },
        required: ['visible'],
      },
    },
  }
}

/**
 * Tool definition for highlighting a geographic region.
 */
export function getHighlightRegionTool(): LLMTool {
  return {
    type: 'function',
    function: {
      name: 'highlight_region',
      description: 'Highlight a geographic region on the globe. You can provide a region name (e.g. "Brazil", "Amazon Basin", "Mediterranean Sea") to use a built-in bounding box, or provide raw GeoJSON for custom shapes. Prefer using "name" over "geojson" — it is simpler and always works for well-known regions.',
      parameters: {
        type: 'object',
        properties: {
          name: {
            type: 'string',
            description: 'A well-known region name (country, continent, ocean, geographic feature). Examples: "Brazil", "Amazon Basin", "Pacific Ocean", "Sahara Desert", "Ring of Fire"',
          },
          geojson: {
            type: 'object',
            description: 'A GeoJSON Feature or FeatureCollection with Polygon/MultiPolygon geometry. Only needed for custom shapes — prefer "name" for well-known regions.',
          },
          label: { type: 'string', description: 'Optional display label for the highlighted region' },
        },
      },
    },
  }
}

/**
 * Build a text summary of the current map view context for the LLM system prompt.
 */
export function buildViewContextSection(viewContext: MapViewContext | null): string {
  if (!viewContext) return ''

  const parts: string[] = []
  const { center, zoom, bounds, visibleCountries, visibleOceans } = viewContext

  parts.push(`Globe center: ${Math.abs(center.lat).toFixed(1)}°${center.lat >= 0 ? 'N' : 'S'}, ${Math.abs(center.lng).toFixed(1)}°${center.lng >= 0 ? 'E' : 'W'}`)
  parts.push(`Zoom level: ${zoom.toFixed(1)}`)
  const fmtLng = (v: number) => `${Math.abs(v).toFixed(1)}°${v >= 0 ? 'E' : 'W'}`
  const fmtLat = (v: number) => `${Math.abs(v).toFixed(1)}°${v >= 0 ? 'N' : 'S'}`
  parts.push(`Viewport bounds: [${fmtLng(bounds.west)}, ${fmtLat(bounds.south)}, ${fmtLng(bounds.east)}, ${fmtLat(bounds.north)}]`)

  if (visibleCountries.length > 0) {
    parts.push(`Visible countries/regions: ${visibleCountries.slice(0, 15).join(', ')}${visibleCountries.length > 15 ? ` (+${visibleCountries.length - 15} more)` : ''}`)
  }
  if (visibleOceans.length > 0) {
    parts.push(`Visible oceans/seas: ${visibleOceans.join(', ')}`)
  }

  return `\n## Geographic Context\n${parts.join('\n')}`
}

// --- §A6: answering about values ---------------------------------------

/**
 * The narrow carve-out from "do not invent data values".
 *
 * STRICT RULE 2 and the legend rule below it forbid Orbit from stating
 * numbers, and they are right to: until the data-encoded path shipped
 * there was no way for it to know one, so every number it could produce
 * came from training-time knowledge about a dataset it was only
 * guessing at. Those rules stay exactly as they are.
 *
 * What changes is that one source of numbers is now real. The rule
 * added here is deliberately about *provenance*, not about topic:
 * numbers that came back in a tool result may be stated, numbers from
 * anywhere else remain forbidden. Widening it to "you may discuss
 * values" would be a regression dressed as a feature — the model would
 * fill in the gaps between tool calls from memory, and the answers
 * would be indistinguishable from the real ones.
 *
 * Appended only when the tools are actually offered, so a picture
 * dataset never sees a permission it has no way to exercise.
 */
export const ANALYSIS_PROMPT_CARVE_OUT = `
## Answering about values (this dataset only)

The dataset on screen carries real values, and you have tools that read them: \`probe_value\`, \`summarize_region\` and \`find_extremum\`.

**The carve-out from STRICT RULE 2 is narrow and about where a number came from, not what it is about.** A number that came back in a tool result this turn is real and you may state it. A number from anywhere else — your own knowledge, an estimate, a legend image, an inference from the colours — remains forbidden exactly as before. If you did not call a tool, you do not have a number.

### Which question calls which tool

| The user asks | You call |
|---|---|
| "where is it worst / highest / strongest / heaviest / most intense?" | \`find_extremum({ kind: "max" })\` |
| "where is it lowest / cleanest / weakest?" | \`find_extremum({ kind: "min" })\` |
| "how much / how bad / how heavy is it over <place>?" | \`summarize_region({ region_name: "<place>" })\` |
| "what's the average / typical / range?" | \`summarize_region()\` |
| "what is it at <place / these coordinates>?" | \`probe_value({ lat, lon })\` |
| "is it worse in A or B?" | \`summarize_region\` once per place, then compare the numbers |

**A question about the values of the dataset already on screen is NOT a discovery question.** Do not answer it by calling \`search_datasets\` and offering a different dataset to load. The user is asking what *this* data says; measure it and tell them. You may mention a related dataset afterwards, but only after you have answered the question that was asked.

**Your whole answer is: call the tool, then say what came back.** No opening impression of where the values look high, no sentence about being about to check, no narration of the call. The first words the user reads should already be the measured answer.

- **Call a tool rather than estimating.** "How much smoke is over Colorado?" means \`summarize_region({ region_name: "colorado" })\`, not a guess from the picture.
- **Say the value by quoting \`valueText\` (or \`meanText\`) EXACTLY.** It arrives already written the way it should be said — number, units, and an "at least" when the field is clipping. Copy the whole string into your sentence unchanged: do not round it, do not re-express it in another unit, do not translate the units into more familiar ones. The units in that string are the ones this dataset publishes. If they look unusual for the subject, they are still correct — two datasets measuring the same phenomenon routinely carry different units, and the one you have measured is the one on screen.
- **A value belongs to the dataset it was measured from.** If you mention any other dataset in the same reply, do not attach a measured number to it — you have measured only the one on screen.
- **Respect \`precision\`.** Every result carries a quantisation note. Do not state more digits than it allows, and do not present a single extreme value as more exact than the note says it is.
- **Say when the number is FROM.** Every result carries \`frameTime\` — the instant the globe is showing. Name it. These are animations; a value with no time is a claim about an unnamed instant. (What the number is *of* is already inside \`valueText\`: quoting that string whole states the area the search covered, which is why it must not be trimmed.)
- **Search the whole dataset unless the user named a place.** "Where is it worst?" is a question about everything on screen. Narrowing to a region without being asked produces a smaller number somewhere else entirely, and the user has no way to tell.
- **Honour \`plateau\` when it appears.** The extreme value is shared by many cells, so there is no single worst *spot* — describe it as worst across an area, and treat the coordinates as a representative point rather than a pinpoint.
- **Honour \`saturated\` when it appears.** The field is clipping at the top of its scale, so the number is a floor: say "at least", not an exact figure.
- **Read \`coverage\` and any \`caveat\` out loud.** A mean over a quarter of a region is a different claim from a mean over all of it, and the user cannot see the difference unless you say it.
- **\`noData: true\` means the dataset covers that point and reports nothing there.** That is not "a low value" and not "outside coverage" — say the dataset shows nothing there.
- **When a tool returns \`ok: false\`, say what it says.** Do not retry with a different region hoping for a number, and do not fall back to describing the colours.
- **\`find_extremum\` moves the globe for you.** The app flies to the coordinates and drops a labelled pin as soon as the tool returns. Do NOT call \`fly_to\` or \`add_marker\` yourself for that result, and do not emit a \`<<FLY:...>>\` marker for it — you would send the globe somewhere a second time. Just say where it is and what the value is; the user is already looking at the place.
- If you do call \`fly_to\` for some *other* reason, **pass \`lat\` and \`lon\` as signed decimal degrees.** Western longitudes and southern latitudes are negative numbers. A compass letter belongs in your prose and never in a tool argument; the same figure written with a W instead of a minus sign puts the camera on the opposite side of the planet.

Answer in prose. You cannot draw a chart in chat, and you should not try to render one with characters.`

/**
 * `probe_value` — the value at one point.
 *
 * Offered only when a data-encoded frame is readable; see
 * `docentAnalysisTools.isAnalysisAvailable`.
 */
export function getProbeValueTool(): LLMTool {
  return {
    type: 'function',
    function: {
      name: 'probe_value',
      description: 'Read the value of the currently loaded dataset at one geographic point, from the frame on screen. Use this for "what is it at X?" questions. Returns the value, its units, whether the point carries no data, and a note on how precisely the value can be stated.',
      parameters: {
        type: 'object',
        properties: {
          lat: { type: 'number', description: 'Latitude in degrees, -90 to 90.' },
          lon: { type: 'number', description: 'Longitude in degrees, -180 to 180.' },
        },
        required: ['lat', 'lon'],
      },
    },
  }
}

/**
 * `summarize_region` — the statistics bundle over an area.
 *
 * `region_name` is checked against the same table the `<>` highlight
 * marker uses, so a name that works in one works in the other.
 */
export function getSummarizeRegionTool(): LLMTool {
  return {
    type: 'function',
    function: {
      name: 'summarize_region',
      description: 'Compute area-weighted statistics for the currently loaded dataset over a region of the frame on screen: mean, median, min, max, 10th and 90th percentiles, how much of the region carries data, and the area that does. Use this for "how much / how bad / what is the average" questions. Omit every argument to summarize the whole dataset.',
      parameters: {
        type: 'object',
        properties: {
          region_name: {
            type: 'string',
            description: 'A named region — a continent, ocean, country or US state (e.g. "colorado", "the arctic", "mexico"). Prefer this over a bbox when the user named a place. An unrecognised name is an error, not a fall back to the whole dataset.',
          },
          bbox: {
            type: 'object',
            description: 'An explicit bounding box, for an area with no name.',
            properties: {
              north: { type: 'number' },
              south: { type: 'number' },
              west: { type: 'number' },
              east: { type: 'number' },
            },
          },
          region: {
            type: 'string',
            enum: ['view'],
            description: 'Pass "view" to summarize only what is currently on screen.',
          },
        },
      },
    },
  }
}

/**
 * `find_extremum` — where the field peaks or bottoms out.
 *
 * The plan flags this as the most noise-sensitive statistic available
 * and the one most likely to be quoted back, which is why its result
 * carries the same precision note as the others and the carve-out
 * above tells the model to respect it.
 */
export function getFindExtremumTool(): LLMTool {
  return {
    type: 'function',
    function: {
      name: 'find_extremum',
      description: 'Find where the currently loaded dataset is at its highest or lowest in the frame on screen, and by how much. Returns latitude, longitude, the value and its units. Use this for "where is it worst / strongest / lowest" questions. The app flies the globe to the result and drops a pin by itself — do NOT call `fly_to` or `add_marker` afterwards.',
      parameters: {
        type: 'object',
        properties: {
          kind: {
            type: 'string',
            enum: ['max', 'min'],
            description: 'Which extreme to find. Defaults to "max".',
          },
          region_name: {
            type: 'string',
            description: 'Optional named region to search within, from the same set `summarize_region` accepts. Omit it — the default is the whole dataset, which is what "where is it worst?" asks. Passing a region narrows the search and yields a different, smaller answer, so pass one ONLY when the user named a place.',
          },
          bbox: {
            type: 'object',
            description: 'Optional explicit bounding box to search within. Same rule as `region_name`: omit it unless the user asked about a specific area.',
            properties: {
              north: { type: 'number' },
              south: { type: 'number' },
              west: { type: 'number' },
              east: { type: 'number' },
            },
          },
        },
      },
    },
  }
}
